import * as Tone from 'tone';
import { createPitchTracker } from '../../utils/pitchTracker';
import { buildSlots, sungFrames, estimateLag, refineLag } from '../../utils/pitchGrading';
import { sectionStart } from '../../utils/syncedTake';

/**
 * Syncs a recording made on an audio context of its own (the scratch
 * recorder's) with the song, so the singing of a highlighted section can
 * be played back on its own (see syncedTake.js).
 *
 * The two audio clocks are bridged by reading both as each chunk arrives
 * (the median of the latest readings). Every 10 ms frame is placed in the song
 * through the Transport's position at the bridged time, following starts,
 * pauses, stops and loops as graded takes do. When the take ends, how far
 * the voice trails the song is measured from the singing against my
 * part's notes, as for graded takes, which also absorbs any error in the
 * bridge; without my part it is a typical latency.
 */

// Clock readings the bridge keeps: the latest few, so it follows at once
// when one context's audio stalls and its clock falls behind (a reading
// is now and then a few ms off, which the median ignores)
const BRIDGE_READINGS = 9;
// The lag is measured from at most the last minute
const LAG_WINDOW_FRAMES = 6000;

const median = (values) => [...values].sort((a, b) => a - b)[values.length >> 1];

/** The highlighted (looping) section as song quarter notes { from, to }, or null. */
export function highlightedSection(controls) {
  const audio = controls?.audioHook;
  const events = audio?.loopEventsRef.current;
  if (!controls?.isLoopMode || !events?.length) return null;
  const quarterSeconds = audio.ticksToBakedTime(Tone.Transport.PPQ);
  const from = sectionStart(events, true, quarterSeconds);
  return { from, to: from + audio.loopDurationRef.current / quarterSeconds };
}

/**
 * Starts syncing a recording on `context`: push(samples, frame) each chunk
 * (frame: its first sample's frame on that context), then finish() for the
 * synced take (null if the song never played) or cancel().
 */
export function createSyncedCapture({ context, controls: c }) {
  const audio = c.audioHook;
  const sectionNow = () => {
    const looping = c.isLoopMode && audio.loopEventsRef.current.length > 0;
    return sectionStart(looping ? audio.loopEventsRef.current : [], looping, audio.ticksToBakedTime(Tone.Transport.PPQ));
  };
  // Song places are kept as seconds at this tempo from the start of the
  // song, so a different section played later in the same take still fits
  const quarterSeconds = audio.ticksToBakedTime(Tone.Transport.PPQ);
  const startOffset = sectionNow() * quarterSeconds;

  // My part's notes being played now, to measure the lag against
  const looping = c.isLoopMode && audio.loopEventsRef.current.length > 0;
  const events = looping ? audio.loopEventsRef.current : audio.noteEventsRef.current;
  const mine = String(c.selectedPart);
  const notes = [];
  if (c.selectedPart !== null && c.selectedPart !== undefined) {
    events.forEach((event) => {
      if (event.pitch === 'REST' || String(event.partId) !== mine || !event.sourceNote) return;
      const midi = Tone.Frequency(event.pitch).toMidi();
      if (Number.isFinite(midi)) notes.push({ start: event.time + startOffset, end: event.time + event.duration + startOffset, midi });
    });
  }

  const sampleRate = context.sampleRate;
  const tracker = createPitchTracker(sampleRate);
  const hopSeconds = tracker.hopSeconds;
  const output = Number.isFinite(c.audioLatency) && c.audioLatency > 0 ? c.audioLatency : 0.03;
  const take = {
    chunks: [],
    frames: [],
    positions: [],
    sampleRate,
    hopSeconds,
    quarterSeconds,
    songOffset: 0,
    lagFrames: Math.round(Math.min(0.3, output + 0.05) / hopSeconds)
  };

  // Transport starts, stops and pauses, walked in step with the frames
  const log = [];
  const handlers = ['start', 'stop', 'pause'].map(type => [type, (time) => { log.push({ time, type }); }]);
  handlers.forEach(([type, handler]) => Tone.Transport.on(type, handler));
  const running = Tone.Transport.state === 'started';
  const walk = { cursor: 0, state: running ? 'started' : 'stopped', newRun: running, pass: -1, lastTicks: 0 };
  const positionAt = (t) => {
    while (walk.cursor < log.length && log[walk.cursor].time <= t) {
      const { type } = log[walk.cursor++];
      if (type === 'start') {
        if (walk.state === 'stopped') walk.newRun = true;
        walk.state = 'started';
      } else {
        walk.state = type === 'pause' ? 'paused' : 'stopped';
      }
    }
    if (walk.state !== 'started') return null;
    const ticks = Tone.Transport.getTicksAtTime(t);
    if (walk.newRun || ticks < walk.lastTicks) {
      walk.pass++;
      walk.newRun = false;
    }
    walk.lastTicks = ticks;
    return { pass: walk.pass, time: (ticks / Tone.Transport.PPQ + sectionNow()) * quarterSeconds };
  };

  const readings = [];
  const tone = Tone.getContext().rawContext;
  const dispose = () => handlers.forEach(([type, handler]) => Tone.Transport.off(type, handler));

  return {
    push(samples, frame) {
      take.chunks.push({ frame, samples });
      readings.push(tone.currentTime - context.currentTime);
      if (readings.length > BRIDGE_READINGS) readings.shift();
      const offset = median(readings);
      for (const result of tracker.push(samples, frame)) {
        take.frames.push(result);
        take.positions.push(positionAt(result.sample / sampleRate + offset));
      }
    },
    finish() {
      dispose();
      if (!take.positions.some(Boolean)) return null;
      if (notes.length) {
        const from = Math.max(0, take.frames.length - LAG_WINDOW_FRAMES);
        const recent = {
          frames: take.frames.slice(from),
          positions: take.positions.slice(from),
          slots: buildSlots(notes),
          hopSeconds
        };
        recent.sung = sungFrames(recent.frames, hopSeconds);
        const estimate = estimateLag({ ...recent, priorFrames: take.lagFrames });
        take.lagFrames = refineLag({ ...recent, lagFrames: estimate });
      }
      return take;
    },
    cancel: dispose
  };
}
